import * as React from 'react';
import { FileRequestLinkPlaceholder, FileSendLinkPlaceholder } from '../../../../models/Token';
import { Popover, PopoverAnchor, PopoverContent } from '../../../components-v2/Popover';
import {
	FileRequestDetailsPanel,
	type FileRequestDetailsPanelProps,
	FileSendDetailsPanel,
	type FileSendDetailsPanelProps,
} from '../FileRequestDetailsPanel';
import { styleSheet } from './styles';
import { useChipPopover } from './useChipPopover';

const mergeSymbolSelector = (placeholder: { symbol: string; additionalSymbols?: string[] }) =>
	[placeholder.symbol, ...(placeholder.additionalSymbols ?? [])]
		.map(symbol => `span[data-merge-symbol="${symbol}"]`)
		.join(', ');

/** The `{{file request}}` chip in the TinyMCE editors. */
export const RICH_CONTENT_FILE_REQUEST_CHIP_SELECTOR = `span[data-placeholder-value="${FileRequestLinkPlaceholder.value}"]`;
/** The `{{send files}}` chip in the TinyMCE editors. */
export const RICH_CONTENT_FILE_SEND_CHIP_SELECTOR = `span[data-placeholder-value="${FileSendLinkPlaceholder.value}"]`;
/** The `{{file request}}` chip in the texting composer. */
export const TEXT_FILE_REQUEST_CHIP_SELECTOR = mergeSymbolSelector(FileRequestLinkPlaceholder);
/** The `{{send files}}` chip in the texting composer. */
export const TEXT_FILE_SEND_CHIP_SELECTOR = mergeSymbolSelector(FileSendLinkPlaceholder);

interface SaveProps {
	/** Fires once when the popover closes after the sender edited it, for surfaces that save as the sender goes. */
	onBlur?: () => void;
}

export type FileRequestDetailsProps = FileRequestDetailsPanelProps & SaveProps;
export type FileSendDetailsProps = FileSendDetailsPanelProps & SaveProps;

/** An email composed from an account template shares the template's request, so its details are the template's. */
export const templateFileRequestDetails = (
	value: FileRequestDetailsPanelProps['value'],
	templateName: string | undefined,
	subject: string | undefined
): FileRequestDetailsProps => ({
	value,
	onChange: () => undefined,
	subject,
	readonly: true,
	note: `Every email sent from the template${templateName ? ` "${templateName}"` : ''} shares this request. Edit the template to change it.`,
});

type ChipProps = Parameters<typeof useChipPopover>[0];

function ChipDetailsPopover({
	root,
	frame,
	chipSelector,
	isEditorFocused,
	onBlur,
	children,
}: ChipProps & SaveProps & { children: (markEdited: () => void) => React.ReactNode }) {
	const editedRef = React.useRef(false);
	const { isOpen, anchorRef, close, contentProps } = useChipPopover({
		root,
		frame,
		chipSelector,
		isEditorFocused,
		// Saving once per edited visit keeps surfaces that save on blur from sending a request per field.
		onClose: () => {
			if (editedRef.current) {
				editedRef.current = false;
				onBlur?.();
			}
		},
	});

	return (
		<Popover
			open={isOpen}
			onOpenChange={open => {
				if (!open) {
					close();
				}
			}}
		>
			<PopoverAnchor virtualRef={anchorRef} />
			<PopoverContent
				side='bottom'
				align='start'
				styles={[styleSheet.content]}
				// Hovering a chip must not pull the cursor out of the editor.
				onOpenAutoFocus={e => e.preventDefault()}
				onCloseAutoFocus={e => e.preventDefault()}
				onMouseEnter={contentProps.onMouseEnter}
				onMouseLeave={contentProps.onMouseLeave}
				onFocus={contentProps.onFocus}
				onBlur={contentProps.onBlur}
			>
				{children(() => {
					editedRef.current = true;
				})}
			</PopoverContent>
		</Popover>
	);
}

export function FileRequestDetailsPopover({
	root,
	frame,
	chipSelector,
	isEditorFocused,
	onBlur,
	...panelProps
}: ChipProps & FileRequestDetailsProps) {
	return (
		<ChipDetailsPopover
			root={root}
			frame={frame}
			chipSelector={chipSelector}
			isEditorFocused={isEditorFocused}
			onBlur={onBlur}
		>
			{markEdited => (
				<FileRequestDetailsPanel
					{...panelProps}
					onChange={value => {
						markEdited();
						panelProps.onChange(value);
					}}
				/>
			)}
		</ChipDetailsPopover>
	);
}

export function FileSendDetailsPopover({
	root,
	frame,
	chipSelector,
	isEditorFocused,
	onBlur,
	...panelProps
}: ChipProps & FileSendDetailsProps) {
	return (
		<ChipDetailsPopover
			root={root}
			frame={frame}
			chipSelector={chipSelector}
			isEditorFocused={isEditorFocused}
			onBlur={onBlur}
		>
			{markEdited => (
				<FileSendDetailsPanel
					{...panelProps}
					onChange={value => {
						markEdited();
						panelProps.onChange(value);
					}}
				/>
			)}
		</ChipDetailsPopover>
	);
}
